import { z } from 'zod';
import { digestSchema } from '../hashing/hash.js';
import { signatureEvidenceSchema } from './signature-evidence.js';
import { stepSectionSchema, stepStateSchema } from './contract.js';
/**
 * What `GET /api/people/{id}/steps/{instanceId}/answers` returns (ADR-013 §4, #209).
 *
 * Defined here so the client parses responses with the schema the API validated against
 * (CLAUDE.md rule 3), and so HR's two tabs and the portal's own "Saved" checks read one shape.
 */
export const stepAttemptSchema = z
    .object({
    attempt: z.number().int().nonnegative(),
    submittedAt: z.string().datetime(),
    artifactDigests: z.array(digestSchema),
})
    .strict();
/**
 * One row of `GET /api/people/{id}/steps` — the person record's form list (ADR-013 §4, #211).
 *
 * `config` is carried so a row can open the answers panel without a second round trip, and because
 * the pinned config is the only thing that says what this instance actually asks. It is the same
 * erased `unknown` the step contract uses everywhere else.
 */
export const personStepSummarySchema = z
    .object({
    id: z.string().min(1),
    type: z.string().min(1),
    state: stepStateSchema,
    attempt: z.number().int().nonnegative(),
    config: z.unknown(),
    startedAt: z.string().datetime().nullable(),
    submittedAt: z.string().datetime().nullable(),
    reviewedAt: z.string().datetime().nullable(),
    /**
     * Health or I-9. A row only ever reaches a caller who may read it, so this is for PRESENTATION
     * — badging the row — and never a permission signal the client is trusted to honour.
     */
    segregated: z.boolean(),
    /**
     * The form's person-visible name and its group in 3.1 (#372 part 3), from the definition the
     * instance pinned. Both nullable: a pinned definition that is no longer seeded still has an
     * instance, and a row with no name is listed under its type rather than dropped.
     */
    heading: z.string().nullable().default(null),
    portalGroup: z.string().nullable().default(null),
    /**
     * WHICH POLICY REQUIRES THIS FORM — the first citation of the requirement the pinned definition
     * satisfies, or null where it closes none (#357, frame `323:1347`).
     *
     * NOT the frame's own second line. Paul writes those by hand — "Front and back", "Because
     * Marcus drives on Agency business", "3 questions, then sign" — and that copy exists nowhere in
     * the catalog. #357 assumed the column could read from `citations`; it can give the SOURCE,
     * which is what the handoff's own target-list column is called, but not his sentences.
     *
     * The first citation only. A row carries up to six, and a list of them under every form is
     * noise where HR wants the one policy to look up.
     */
    source: z.string().nullable().default(null),
})
    .strict();
export const personStepsSchema = z.object({ steps: z.array(personStepSummarySchema) }).strict();
export const stepAnswersSchema = z
    .object({
    state: stepStateSchema,
    /** Zero-based, like `SubmissionRecord.attempt` and `StepInstance.attempt`. */
    attempt: z.number().int().nonnegative(),
    /**
     * The draft while there is one, else the submission. NEVER validated: a draft is a scratchpad
     * (ADR-013 §3), so everything downstream must tolerate a half-typed one.
     */
    value: z.unknown(),
    sections: z.array(stepSectionSchema),
    startedAt: z.string().datetime().nullable(),
    savedAt: z.string().datetime().nullable(),
    submittedAt: z.string().datetime().nullable(),
    /** When HR decided. Set on approval AND on rejection, and never cleared afterwards. */
    reviewedAt: z.string().datetime().nullable(),
    /**
     * WHO decided, as an OPAQUE PRINCIPAL ID — `CurrentUser.userId`, which is what
     * `StepInstance.reviewedBy` has always stored. It is not a name and not an email.
     *
     * Carried so the banner rests on recorded evidence and so a caller can correlate a decision
     * with its `step.approved` audit row, which records the same id. ADR-013 §4 asks the banner to
     * read "Reviewed by … on …", and the reason it currently shows only the date is written down
     * beside the banner in `StepAnswersPanel`: nothing in this system records a reviewer's display
     * name, so there is no name to put there without inventing one.
     */
    reviewedBy: z.string().min(1).nullable(),
    attempts: z.array(stepAttemptSchema),
    /**
     * THE SIGNING EVIDENCE for the attempt this payload shows (ADR-014 §3, #215 item 5).
     *
     * ADR-014 puts it here and nowhere else: "It is not shown in lists or the timeline. HR sees it
     * on the form's `Answers so far` tab under 'Signing details', with the step's own capability."
     * It carries an IP address and a user agent, which is why it is not in the audit trail and not
     * on any list endpoint — this route is the one place it is served, behind the same gate as the
     * answers themselves, which for a health or I-9 form is `answers.restricted.read`.
     *
     * NULL IS THE ORDINARY CASE, not an error, and it means three different things that the reader
     * cannot and need not distinguish: the step type does not produce a signature; the attempt on
     * screen is a draft that has not been signed yet; or the instance predates #215. A panel that
     * treats null as "something is wrong" would be wrong three times out of three.
     */
    signingDetails: signatureEvidenceSchema.nullable(),
})
    .strict();
//# sourceMappingURL=answers.js.map